/**
 * The account session, as the screens and the shell see it.
 *
 * The session is server state: the backend holds it, `GET /api/me` reports it,
 * and every screen reads the same cached answer under one query key, so the
 * navbar and the screen beneath it can never disagree about who is signed in.
 * Signing out clears the whole cache, because everything in it belongs to the
 * account that has just left (FR-005, FR-006).
 *
 * @author John Grimes
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";

import { apiJson, apiVoid, isApiError } from "../../lib/api/apiClient";
import { PLAIN_ERROR_MESSAGES } from "../../lib/fhir/operationOutcome";

import type { Me, PlainError } from "shared/api";

/** The key the signed-in account is cached under. */
export const SESSION_QUERY_KEY = ["me"];

/** The key the account's passkeys are cached under. */
export const PASSKEYS_QUERY_KEY = ["passkeys"];

/** What the session hook reports. */
export type SessionState = {
  /** The signed-in account, once it is known. */
  me?: Me;
  /** True while the answer is still being fetched. */
  isPending: boolean;
  /** Why the session could not be read, in plain language. */
  error?: PlainError;
};

/** Options for {@link useSession}. */
export type SessionOptions = {
  /**
   * False on screens that must work signed out, so the shell does not ask the
   * backend who is signed in before anybody could be.
   */
  enabled?: boolean;
};

/** What the sign-out hook hands back. */
export type SignOutControl = {
  /** Ends the session and returns to the sign-in screen. */
  signOut: () => void;
  /** True while the request is in flight. */
  isPending: boolean;
};

/**
 * Describes a thrown value in plain language. Failures from the API client
 * already carry their copy; anything else gets the general message rather than
 * its own words, which are written for developers (Principle IV).
 *
 * @param error - Whatever was thrown.
 * @returns The message to show, with any technical detail kept for the
 *   disclosure.
 *
 * @example
 * ```ts
 * onError: (error) => setFailure(plainErrorOf(error)),
 * ```
 */
export const plainErrorOf = (error: unknown): PlainError => {
  if (isApiError(error)) return error.plain;
  return {
    message: PLAIN_ERROR_MESSAGES.unknown,
    ...(error instanceof Error
      ? { detail: `${error.name}: ${error.message}` }
      : {}),
  };
};

/**
 * Reports who is signed in.
 *
 * @param options - Options for the hook.
 * @param options.enabled - False to skip the request altogether.
 * @returns The session state.
 *
 * @example
 * ```tsx
 * const { me, isPending } = useSession();
 * if (isPending) return <p>Checking that you are signed in…</p>;
 * return <p>Signed in as {me?.username}</p>;
 * ```
 */
export const useSession = ({
  enabled = true,
}: SessionOptions = {}): SessionState => {
  const { data, isPending, error } = useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: () => apiJson<Me>("/me"),
    enabled,
    retry: false,
  });

  return {
    ...(data === undefined ? {} : { me: data }),
    isPending,
    ...(error === null ? {} : { error: plainErrorOf(error) }),
  };
};

/**
 * Ends the session. The cache is emptied whether or not the backend answered:
 * the browser must not keep showing one account's connections, settings or
 * drafts after the user has asked to leave (FR-006).
 *
 * @returns The sign-out control.
 *
 * @example
 * ```tsx
 * const { signOut, isPending } = useSignOut();
 * <button type="button" onClick={signOut} disabled={isPending}>Sign out</button>
 * ```
 */
export const useSignOut = (): SignOutControl => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { mutate, isPending } = useMutation({
    mutationFn: () => apiVoid("/auth/logout", { method: "POST" }),
    onSettled: async () => {
      queryClient.clear();
      await navigate("/sign-in", { replace: true });
    },
  });

  return { signOut: mutate, isPending };
};
